import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Alert,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    Skeleton,
} from '@mui/material';
import apiService from '../services/api';

interface ProgramAnalysisData {
    _id: string;
    name: string;
    podcast: {
        _id: string;
        title: string;
    };
    contentCount: number;
    totalViews: number;
    totalLikes: number;
    totalComments: number;
    avgViews: number;
    engagementRate: number;
    uniquePaidBrandsCount: number;
    dateRange: {
        earliest: string;
        latest: string;
    };
}

const ProgramAnalysisLimited: React.FC = () => {
    // Fixed week: November 3-9, 2025 (Monday to Sunday)
    const FIXED_WEEK = '2025-11-03';
    const FIXED_WEEK_LABEL = 'Nov 3 - Nov 9, 2025';

    // State
    const [orderBy, setOrderBy] = useState<string>('totalViews');
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');

    // Fetch programs data for the fixed week
    const {
        data: programsData,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['programs-analysis-limited', { week: FIXED_WEEK }],
        queryFn: () => apiService.getProgramsAnalysis(
            FIXED_WEEK,
            {},
            undefined
        ),
        staleTime: 2 * 60 * 1000, // 2 minutes
    });

    const programs = useMemo(() =>
        (programsData?.programs as ProgramAnalysisData[]) || [],
        [programsData?.programs]
    );

    // Client-side sorting
    const sortedPrograms = useMemo(() => {
        const comparator = (a: ProgramAnalysisData, b: ProgramAnalysisData) => {
            let aValue: any;
            let bValue: any;

            switch (orderBy) {
                case 'name':
                    aValue = a.name.toLowerCase();
                    bValue = b.name.toLowerCase();
                    break;
                case 'podcast':
                    aValue = a.podcast.title.toLowerCase();
                    bValue = b.podcast.title.toLowerCase();
                    break;
                case 'contentCount':
                    aValue = a.contentCount;
                    bValue = b.contentCount;
                    break;
                case 'totalViews':
                    aValue = a.totalViews;
                    bValue = b.totalViews;
                    break;
                case 'avgViews':
                    aValue = a.avgViews;
                    bValue = b.avgViews;
                    break;
                case 'totalLikes':
                    aValue = a.totalLikes;
                    bValue = b.totalLikes;
                    break;
                case 'totalComments':
                    aValue = a.totalComments;
                    bValue = b.totalComments;
                    break;
                case 'engagementRate':
                    aValue = a.engagementRate;
                    bValue = b.engagementRate;
                    break;
                case 'uniquePaidBrandsCount':
                    aValue = a.uniquePaidBrandsCount;
                    bValue = b.uniquePaidBrandsCount;
                    break;
                default:
                    aValue = a.totalViews;
                    bValue = b.totalViews;
            }

            if (typeof aValue === 'string') {
                return order === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }

            return order === 'asc' ? aValue - bValue : bValue - aValue;
        };

        return [...programs].sort(comparator);
    }, [programs, orderBy, order]);

    // Calculate totals
    const totals = useMemo(() => {
        return {
            totalPrograms: programs.length,
            totalContent: programs.reduce((sum, p) => sum + p.contentCount, 0),
            totalViews: programs.reduce((sum, p) => sum + p.totalViews, 0),
            totalLikes: programs.reduce((sum, p) => sum + p.totalLikes, 0),
            totalComments: programs.reduce((sum, p) => sum + p.totalComments, 0),
            totalUniqueBrands: programs.reduce((sum, p) => sum + p.uniquePaidBrandsCount, 0),
            avgEngagement: programs.length > 0
                ? programs.reduce((sum, p) => sum + p.engagementRate, 0) / programs.length
                : 0,
        };
    }, [programs]);

    const handleRequestSort = (property: string) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const formatNumber = (num: number): string => {
        if (num >= 1000000) {
            return `${(num / 1000000).toFixed(1)}M`;
        }
        if (num >= 1000) {
            return `${(num / 1000).toFixed(1)}K`;
        }
        return num.toString();
    };

    return (
        <Container maxWidth={false} sx={{ py: 4 }}>
            <Box mb={4}>
                <Typography variant="h1" component="h1" gutterBottom fontWeight="bold">
                    Explorar programas
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {FIXED_WEEK_LABEL}
                </Typography>
            </Box>

            {/* Summary Stats */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" color="primary">
                                {totals.totalPrograms}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Programas
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" color="secondary">
                                {totals.totalContent}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Episodios
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" color="success.main">
                                {formatNumber(totals.totalViews)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total Views
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" color="info.main">
                                {formatNumber(totals.totalLikes)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total Likes
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" color="warning.main">
                                {formatNumber(totals.totalComments)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total Comments
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" sx={{ color: '#9C27B0' }}>
                                {totals.totalUniqueBrands}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Marcas
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Table */}
            {error ? (
                <Alert severity="error">Failed to load program analysis data</Alert>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell align="center" sx={{ width: 60 }}>#</TableCell>
                                <TableCell sx={{ maxWidth: 200 }}>
                                    <TableSortLabel
                                        active={orderBy === 'name'}
                                        direction={orderBy === 'name' ? order : 'asc'}
                                        onClick={() => handleRequestSort('name')}
                                    >
                                        Program
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ maxWidth: 150 }}>
                                    <TableSortLabel
                                        active={orderBy === 'podcast'}
                                        direction={orderBy === 'podcast' ? order : 'asc'}
                                        onClick={() => handleRequestSort('podcast')}
                                    >
                                        Canal
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="center">
                                    <TableSortLabel
                                        active={orderBy === 'contentCount'}
                                        direction={orderBy === 'contentCount' ? order : 'asc'}
                                        onClick={() => handleRequestSort('contentCount')}
                                    >
                                        Episodios
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="center">
                                    <TableSortLabel
                                        active={orderBy === 'totalViews'}
                                        direction={orderBy === 'totalViews' ? order : 'asc'}
                                        onClick={() => handleRequestSort('totalViews')}
                                    >
                                        Total Views
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="center">
                                    <TableSortLabel
                                        active={orderBy === 'avgViews'}
                                        direction={orderBy === 'avgViews' ? order : 'asc'}
                                        onClick={() => handleRequestSort('avgViews')}
                                    >
                                        Avg Views
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="center">
                                    <TableSortLabel
                                        active={orderBy === 'totalLikes'}
                                        direction={orderBy === 'totalLikes' ? order : 'asc'}
                                        onClick={() => handleRequestSort('totalLikes')}
                                    >
                                        Likes
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="center">
                                    <TableSortLabel
                                        active={orderBy === 'totalComments'}
                                        direction={orderBy === 'totalComments' ? order : 'asc'}
                                        onClick={() => handleRequestSort('totalComments')}
                                    >
                                        Comments
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="center">
                                    <TableSortLabel
                                        active={orderBy === 'uniquePaidBrandsCount'}
                                        direction={orderBy === 'uniquePaidBrandsCount' ? order : 'asc'}
                                        onClick={() => handleRequestSort('uniquePaidBrandsCount')}
                                    >
                                        Paid Brands
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="center">
                                    <TableSortLabel
                                        active={orderBy === 'engagementRate'}
                                        direction={orderBy === 'engagementRate' ? order : 'asc'}
                                        onClick={() => handleRequestSort('engagementRate')}
                                    >
                                        Engagement
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading && programs.length === 0 ? (
                                // Skeleton rows
                                Array.from({ length: 10 }).map((_, index) => (
                                    <TableRow key={`skeleton-${index}`}>
                                        <TableCell align="center">
                                            <Skeleton variant="text" width={30} />
                                        </TableCell>
                                        <TableCell>
                                            <Skeleton variant="text" width={200} />
                                        </TableCell>
                                        <TableCell>
                                            <Skeleton variant="rectangular" width={120} height={24} />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Skeleton variant="text" width={60} />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Skeleton variant="text" width={80} />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Skeleton variant="text" width={80} />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Skeleton variant="text" width={60} />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Skeleton variant="text" width={60} />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Skeleton variant="text" width={60} />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Skeleton variant="text" width={60} />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : sortedPrograms.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={10} align="center">
                                        <Typography variant="body2" color="text.secondary" py={4}>
                                            No programs found with the selected filters
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                sortedPrograms.map((program, index) => (
                                    <TableRow
                                        key={program._id}
                                    >
                                        <TableCell align="center">
                                            <Typography variant="body2" color="text.secondary">
                                                {index + 1}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 200 }}>
                                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                                {program.name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 150 }}>
                                            <Chip
                                                label={program.podcast.title}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                sx={{ maxWidth: 150 }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body2">
                                                {program.contentCount}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body2">
                                                {formatNumber(program.totalViews)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body2">
                                                {formatNumber(Math.round(program.avgViews))}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body2">
                                                {formatNumber(program.totalLikes)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body2">
                                                {formatNumber(program.totalComments)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body2" sx={{ color: '#9C27B0', fontWeight: 500 }}>
                                                {program.uniquePaidBrandsCount}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body2" color="success.main">
                                                {program.engagementRate.toFixed(2)}%
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Container>
    );
};

export default ProgramAnalysisLimited;

